import React from "react";
import { Link } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { useLang } from "@/contexts/LangContext";
import { formatTL } from "@/lib/api";
import { X, Minus, Plus } from "lucide-react";

export default function Cart() {
  const { items, removeItem, updateQty, subtotal } = useCart();
  const { t, lang } = useLang();

  if (items.length === 0) {
    return (
      <div data-testid="cart-empty" className="px-6 py-32 max-w-2xl mx-auto text-center">
        <h1 className="font-heading text-4xl mb-4">{t("cart.title")}</h1>
        <p className="text-sm text-[#52525B] mb-8">{t("cart.empty")}</p>
        <Link to="/products" className="inline-block bg-[#0A0A0A] hover:bg-[#C8A96A] text-white px-10 py-4 text-xs uppercase tracking-[0.25em] transition-colors">
          {t("cart.continue")}
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="px-6 lg:px-16 py-12 max-w-[1440px] mx-auto">
      <h1 className="font-heading text-4xl lg:text-5xl mb-10">{t("cart.title")}</h1>
      <div className="grid lg:grid-cols-[1fr_400px] gap-12">
        <div className="divide-y divide-border">
          {items.map((it) => (
            <div key={it.product_id} data-testid={`cart-item-${it.product_id}`} className="py-6 flex gap-4 lg:gap-6">
              <Link to={`/products/${it.product_id}`} className="w-24 lg:w-32 aspect-square overflow-hidden bg-[#F5F2EC] shrink-0">
                <img src={it.image} alt={it.name_tr} className="w-full h-full object-cover" />
              </Link>
              <div className="flex-1 flex flex-col justify-between">
                <div className="flex justify-between gap-4">
                  <Link to={`/products/${it.product_id}`} className="font-heading text-lg lg:text-xl">{lang === "tr" ? it.name_tr : it.name_en}</Link>
                  <button data-testid={`remove-${it.product_id}`} onClick={() => removeItem(it.product_id)} className="text-[#52525B] hover:text-[#EF4444]"><X size={16} /></button>
                </div>
                <div className="flex justify-between items-end">
                  <div className="flex items-center border border-[#0A0A0A]">
                    <button onClick={() => updateQty(it.product_id, it.quantity - 1)} className="px-2 py-2"><Minus size={12} /></button>
                    <span className="px-4 font-finance text-sm">{it.quantity}</span>
                    <button onClick={() => updateQty(it.product_id, it.quantity + 1)} className="px-2 py-2"><Plus size={12} /></button>
                  </div>
                  <span className="font-finance text-lg">{formatTL(it.price * it.quantity)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <aside data-testid="cart-summary" className="bg-[#F5F2EC] p-8 lg:p-10 h-fit">
          <h2 className="font-heading text-2xl mb-6">{t("cart.title")}</h2>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span>{t("cart.subtotal")}</span><span className="font-finance">{formatTL(subtotal)}</span></div>
            <div className="flex justify-between text-[#52525B]"><span>{t("cart.shipping")}</span><span className="font-finance">{lang === "tr" ? "Ücretsiz" : "Free"}</span></div>
            <div className="flex justify-between pt-3 mt-3 border-t border-[#0A0A0A]/20 text-lg"><span className="font-medium">{t("cart.total")}</span><span data-testid="cart-total" className="font-finance">{formatTL(subtotal)}</span></div>
          </div>
          <Link to="/checkout" data-testid="checkout-btn" className="mt-8 block text-center bg-[#0A0A0A] hover:bg-[#C8A96A] text-white py-4 text-xs uppercase tracking-[0.25em] transition-colors">
            {t("cart.checkout")}
          </Link>
          <Link to="/products" className="mt-3 block text-center text-xs uppercase tracking-[0.25em] underline underline-offset-4 decoration-[#C8A96A]">
            {t("cart.continue")}
          </Link>
        </aside>
      </div>
    </div>
  );
}
